import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useErrorInputs } from "../../hooks/useErrorInputs";
import { useDataAuth } from "../../hooks/useDataAuth";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import moment from 'moment';

import { getSelectData, getContactsConcat, getOpportunities, getFollowedUpBy } from "../../services/meetings.service";
import { MultipleSelectAutoComplete } from '../others/MultipleSelectAutoComplete';
import { GenericAutocomplete } from '../others/GenericAutocomplete';
import { useTranslation } from "react-i18next";



export const CreateFormMeetings = forwardRef(({ dto, handleInputChange, handleDirectInputChange, viewOpportunity = false }, ref) => {
    const { t } = useTranslation();
    const [optionFollowedUpBy, setOptionFollowedUpBy] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [opportunityState, setOpportunity] = useState([]);
    const [options, setOptions] = useState({
        meeting_type: [],
        contacted_through: [],
        meeting_objective: [],
        meeting_status: [],
    });
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();

    const validateField = (name, value) => {
        if (
            value === null ||
            value === undefined ||
            (typeof value === "string" && value.trim() === "") ||
            (Array.isArray(value) && value.length === 0)
        ) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: "" });
            return true;
        }
    };

    const validateForm = () => {
        let hasError = false;
        const requiredFields = [
            "id_contact",
            "meeting_type",
            "comments",
            "meeting_date",
            "meeting_objective",
            "meeting_status"
        ];
        requiredFields.forEach((field) => {
            const isValid = validateField(field, dto[field]);
            if (!isValid) hasError = true;
        });
        return !hasError;
    };

    useImperativeHandle(ref, () => ({
        validateForm
    }));

    useEffect(() => {
        const fetchFollowedUpBy = async () => {
            const resp = await getFollowedUpBy(userSession.token);
            if (resp.data && Array.isArray(resp.data)) {
                resp.data = resp.data.filter(item => ['180', '181', '182', '183', '198', '195', '199'].includes(item.value));
                setOptionFollowedUpBy(resp.data);
            }

            const response = await getSelectData('50', userSession.token);
            if (Array.isArray(response.data)) {
                const parsedOptions = {};
                response.data.forEach((item) => {
                    const field = item.field.trim();
                    try {
                        const parsedValue = JSON.parse(item.value.endsWith("]") ? item.value : `${item.value}]`);
                        parsedOptions[field] = parsedValue;
                    } catch (error) {
                        console.error(`Error parsing JSON for field "${field}":`, error);
                    }
                });
                setOptions(parsedOptions);
            }
            const contactsResponse = await getContactsConcat(userSession.token);
            setContacts(contactsResponse.data);
            const opportunityResponse = await getOpportunities(userSession.token);
            setOpportunity(opportunityResponse.data);
        }
        fetchFollowedUpBy();
    }, [userSession.token]);

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY-MM-DDTHH:mm'));

    return (
        <Box sx={{ width: "100%", mx: "auto", mt: 3 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <GenericAutocomplete
                        options={contacts}
                        value={dto.id_contact}
                        label="Contacto *"
                        name="id_contact"
                        onChange={handleInputChange}
                        error={Boolean(errorInputs.id_contact?.isError)}
                        helperText={errorInputs.id_contact?.messageError}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small" error={errorInputs.meeting_type?.isError}>
                        <InputLabel>Tipo de seguimiento *</InputLabel>
                        <Select
                            name="meeting_type"
                            value={dto.meeting_type}
                            onChange={handleInputChange}
                        >
                            {options.meeting_type.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.meeting_type?.isError && (
                            <FormHelperText>{errorInputs.meeting_type?.messageError}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>
                {!viewOpportunity && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth size="small" error={errorInputs.id_commercial_opportunity?.isError}>
                            <InputLabel>Oportunidad</InputLabel>
                            <Select
                                name="id_commercial_opportunity"
                                value={dto.id_commercial_opportunity}
                                onChange={handleInputChange}
                            >
                                {opportunityState.map((option) => (
                                    <MenuItem key={option.code} value={option.code}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errorInputs.id_commercial_opportunity?.isError && (
                                <FormHelperText>{errorInputs.id_commercial_opportunity?.messageError}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        fullWidth
                        label="Fecha de la reunión"
                        size="small"
                        name="meeting_date"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={dto.meeting_date}
                        onChange={handleInputChange}
                        // onChange={(e) => handleChangeDate(e, 'meeting_date')}
                        error={errorInputs.meeting_date?.isError}
                        helperText={errorInputs.meeting_date?.messageError}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small" error={errorInputs.meeting_objective?.isError}>
                        <InputLabel>Objetivo de la reunión</InputLabel>
                        <Select
                            name="meeting_objective"
                            value={dto.meeting_objective}
                            onChange={handleInputChange}
                        >
                            {options.meeting_objective.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.meeting_objective?.isError && (
                            <FormHelperText>{errorInputs.meeting_objective?.messageError}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small" error={errorInputs.meeting_status?.isError}>
                        <InputLabel>Estado de la reunión</InputLabel>
                        <Select
                            name="meeting_status"
                            value={dto.meeting_status}
                            onChange={handleInputChange}
                        >
                            {options.meeting_status.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {errorInputs.meeting_status?.isError && (
                            <FormHelperText>{errorInputs.meeting_status?.messageError}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm:12 }}>
                    <TextField
                        fullWidth
                        label="Dirección"
                        size="small"
                        name="address"
                        value={dto.address}
                        onChange={handleInputChange}
                        error={errorInputs.address?.isError}
                        helperText={errorInputs.address?.messageError}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm:12 }}>
                    <TextField
                        fullWidth
                        label="Comentarios *"
                        size="small"
                        name="comments"
                        value={dto.comments}
                        onChange={handleInputChange}
                        error={errorInputs.comments?.isError}
                        helperText={errorInputs.comments?.messageError}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm:12 }}>
                    <MultipleSelectAutoComplete
                        label={'Acompañamiento'}
                        data={optionFollowedUpBy}
                        values={typeof dto.followed_up_by === "string" ? dto.followed_up_by === '' ? [] : dto.followed_up_by.split(',') : dto.followed_up_by}
                        handleChange={({ target }) => handleDirectInputChange('followed_up_by', target.value)}
                        name={'followed_up_by'}
                    />
                </Grid>
            </Grid>
        </Box>
    )
})